import React, { useState, useEffect } from 'react';
import './App.css';
import ApiClient, { UserInfo, AppStatus, getDefaultApiHostname, normalizeApiHostname } from './api/client';
import Dashboard from './components/Dashboard';
import ChannelManager from './components/ChannelManager';
import ApiKeyManager from './components/ApiKeyManager';
import SubmissionManager from './components/SubmissionManager';
import IPManagement from './components/IPManagement';
import CostAnalytics from './components/CostAnalytics';
import Analytics from './components/Analytics';
import ReputationManager from './components/ReputationManager';
import Settings from './components/Settings';
import SetupScreen from './components/SetupScreen';
import StatusBanner from './components/StatusBanner';
import DocsPortal from './components/DocsPortal';
import { PermissionProvider, usePermissions } from './context/PermissionContext';

type Tab = 'forms' | 'channels' | 'submissions' | 'apikeys' | 'ipmanagement' | 'reputation' | 'analytics' | 'costs' | 'docs' | 'settings';

interface AppContentProps {
  apiClient: ApiClient;
  appStatus: AppStatus | null;
  hostname: string;
  onReconfigure: () => void;
  onLogout: () => void;
}

const AppContent: React.FC<AppContentProps> = ({ apiClient, appStatus, hostname, onReconfigure, onLogout }) => {
  const { hasPermission, roleLabel } = usePermissions();
  const [connectionOpen, setConnectionOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const saved = localStorage.getItem('activeTab') as Tab | null;
    return saved && ['forms', 'channels', 'submissions', 'apikeys', 'ipmanagement', 'reputation', 'analytics', 'costs', 'docs', 'settings'].includes(saved) ? saved : 'forms';
  });

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Redirect to allowed tab if current tab is not permitted
  useEffect(() => {
    const tabPermissions: Record<Tab, string> = {
      forms: 'forms.view',
      channels: 'channels.view',
      submissions: 'submissions.view',
      apikeys: 'api_keys.view',
      ipmanagement: 'ip.view',
      reputation: 'reputation.view',
      analytics: 'analytics.view',
      costs: 'costs.view',
      docs: 'api_keys.view',
      settings: 'settings.view',
    };

    if (!hasPermission(tabPermissions[activeTab])) {
      // Find first allowed tab
      const allowedTab = (Object.keys(tabPermissions) as Tab[]).find(
        tab => hasPermission(tabPermissions[tab])
      );
      if (allowedTab) {
        setActiveTab(allowedTab);
      }
    }
  }, [activeTab, hasPermission]);

  const navigateToSettings = () => {
    if (hasPermission('settings.view')) {
      setActiveTab('settings');
    }
  };

  const navigateToSubmissions = (params: { ipId?: number; asn?: number }) => {
    if (hasPermission('submissions.view')) {
      // Update URL params before switching tab
      const url = new URL(window.location.href);
      url.searchParams.delete('ipId');
      url.searchParams.delete('asn');
      if (params.ipId) url.searchParams.set('ipId', String(params.ipId));
      if (params.asn) url.searchParams.set('asn', String(params.asn));
      window.history.replaceState({}, '', url.toString());
      setActiveTab('submissions');
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="brand-lockup">
              <img
                src="/form-pipeline-icon.png"
                alt=""
                className="brand-mark"
                aria-hidden="true"
              />
              <div>
                <div className="brand-eyebrow">Operations Console</div>
                <h1>Form Pipeline</h1>
                <p className="subtitle">Managed form routing, moderation, and integrations</p>
              </div>
            </div>
            <div className="connection-menu">
              <button
                type="button"
                className="connection-button"
                onClick={() => setConnectionOpen(open => !open)}
                aria-expanded={connectionOpen}
              >
                <span className="connection-host">{hostname.replace(/^https?:\/\//, '')}</span>
                {roleLabel && <span className="connection-role">{roleLabel}</span>}
              </button>
              {connectionOpen && (
                <div className="connection-popover">
                  <div className="connection-popover-label">Backend</div>
                  <div className="connection-popover-host">{hostname}</div>
                  <div className="connection-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setConnectionOpen(false);
                        onReconfigure();
                      }}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        setConnectionOpen(false);
                        onLogout();
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <StatusBanner
        status={appStatus}
        onNavigateToSettings={hasPermission('settings.view') ? navigateToSettings : undefined}
      />

      <nav className="app-nav">
        <div className="app-nav-inner">
          {hasPermission('forms.view') && (
            <button
              className={`nav-button ${activeTab === 'forms' ? 'active' : ''}`}
              onClick={() => setActiveTab('forms')}
            >
              Forms
            </button>
          )}
          {hasPermission('channels.view') && (
            <button
              className={`nav-button ${activeTab === 'channels' ? 'active' : ''}`}
              onClick={() => setActiveTab('channels')}
            >
              Channels
            </button>
          )}
          {hasPermission('submissions.view') && (
            <button
              className={`nav-button ${activeTab === 'submissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('submissions')}
            >
              Submissions
            </button>
          )}
          {hasPermission('api_keys.view') && (
            <button
              className={`nav-button ${activeTab === 'apikeys' ? 'active' : ''}`}
              onClick={() => setActiveTab('apikeys')}
            >
              API Keys
            </button>
          )}
          {hasPermission('ip.view') && (
            <button
              className={`nav-button ${activeTab === 'ipmanagement' ? 'active' : ''}`}
              onClick={() => setActiveTab('ipmanagement')}
            >
              IP Management
            </button>
          )}
          {hasPermission('reputation.view') && (
            <button
              className={`nav-button ${activeTab === 'reputation' ? 'active' : ''}`}
              onClick={() => setActiveTab('reputation')}
            >
              Reputation
            </button>
          )}
          {hasPermission('analytics.view') && (
            <button
              className={`nav-button ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </button>
          )}
          {hasPermission('costs.view') && (
            <button
              className={`nav-button ${activeTab === 'costs' ? 'active' : ''}`}
              onClick={() => setActiveTab('costs')}
            >
              Costs
            </button>
          )}
          {hasPermission('api_keys.view') && (
            <button
              className={`nav-button ${activeTab === 'docs' ? 'active' : ''}`}
              onClick={() => setActiveTab('docs')}
            >
              Docs
            </button>
          )}
          {hasPermission('settings.view') && (
            <button
              className={`nav-button ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          )}
        </div>
      </nav>

      <main className="app-content">
        {activeTab === 'forms' && hasPermission('forms.view') && (
          <Dashboard apiClient={apiClient} />
        )}
        {activeTab === 'channels' && hasPermission('channels.view') && (
          <ChannelManager apiClient={apiClient} />
        )}
        {activeTab === 'submissions' && hasPermission('submissions.view') && (
          <SubmissionManager apiClient={apiClient} />
        )}
        {activeTab === 'apikeys' && hasPermission('api_keys.view') && (
          <ApiKeyManager apiClient={apiClient} />
        )}
        {activeTab === 'ipmanagement' && hasPermission('ip.view') && (
          <IPManagement apiClient={apiClient} onNavigateToSubmissions={navigateToSubmissions} />
        )}
        {activeTab === 'reputation' && hasPermission('reputation.view') && (
          <ReputationManager apiClient={apiClient} />
        )}
        {activeTab === 'analytics' && hasPermission('analytics.view') && (
          <Analytics apiClient={apiClient} />
        )}
        {activeTab === 'costs' && hasPermission('costs.view') && (
          <CostAnalytics apiClient={apiClient} />
        )}
        {activeTab === 'docs' && hasPermission('api_keys.view') && (
          <DocsPortal apiKeyAvailable={Boolean(localStorage.getItem('apiKey'))} />
        )}
        {activeTab === 'settings' && hasPermission('settings.view') && (
          <Settings apiClient={apiClient} />
        )}
      </main>
    </div>
  );
};

function App() {
  const [apiKey, setApiKey] = useState<string>('');
  const [hostname, setHostname] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [apiClient, setApiClient] = useState<ApiClient | null>(null);
  const [appStatus, setAppStatus] = useState<AppStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load credentials from localStorage
        const storedKey = localStorage.getItem('apiKey');
        const storedHostname = normalizeApiHostname(localStorage.getItem('apiHostname') || getDefaultApiHostname());

        if (!storedKey) {
          setHostname(storedHostname);
          setShowSetup(true);
          setLoading(false);
          return;
        }

        setApiKey(storedKey);
        setHostname(storedHostname);
        const client = new ApiClient(storedKey, storedHostname);
        setApiClient(client);

        // Fetch user info and permissions
        try {
          const info = await client.getMe();
          console.log('User info:', info);
          // Merge with defaults for any missing permission categories
          const isAdmin = info.role === 'admin';
          const mergedPermissions = {
            submissions: info.permissions?.submissions ?? { view: true, moderate: true, delete: true, delete_permanent: isAdmin },
            ip: info.permissions?.ip ?? { view: true, manage: true },
            forms: info.permissions?.forms ?? { view: true, create: true, edit: true, delete: isAdmin },
            channels: info.permissions?.channels ?? { view: true, manage: isAdmin },
            integrations: info.permissions?.integrations ?? { view: true, manage: isAdmin },
            analytics: info.permissions?.analytics ?? { view: true },
            costs: info.permissions?.costs ?? { view: isAdmin },
            api_keys: info.permissions?.api_keys ?? { view: isAdmin, manage: isAdmin },
            settings: info.permissions?.settings ?? { view: isAdmin, manage: isAdmin },
            reputation: info.permissions?.reputation ?? { view: true, manage: isAdmin },
          };
          setUserInfo({ ...info, permissions: mergedPermissions });
        } catch (err) {
          console.error('Failed to fetch user info:', err);
          // Fallback: try old permission check for backwards compatibility
          try {
            const perms = await client.checkPermissions();
            // Create a minimal userInfo from old API
            setUserInfo({
              authenticated: true,
              role: perms.is_admin ? 'admin' : 'user',
              role_label: perms.is_admin ? 'Admin' : 'User',
              permissions: {
                submissions: { view: true, moderate: true, delete: true, delete_permanent: perms.is_admin },
                ip: { view: true, manage: true },
                forms: { view: true, create: true, edit: true, delete: perms.is_admin },
                channels: { view: true, manage: perms.is_admin },
                integrations: { view: true, manage: perms.is_admin },
                analytics: { view: true },
                costs: { view: perms.is_admin },
                api_keys: { view: perms.is_admin, manage: perms.is_admin },
                settings: { view: perms.is_admin, manage: perms.is_admin },
                reputation: { view: true, manage: perms.is_admin },
              },
            });
          } catch (fallbackErr) {
            console.error('Fallback permission check failed:', fallbackErr);
            // Set minimal permissions matching User role
            setUserInfo({
              authenticated: true,
              role: 'user',
              role_label: 'User',
              permissions: {
                submissions: { view: true, moderate: true, delete: true, delete_permanent: false },
                ip: { view: true, manage: true },
                forms: { view: true, create: true, edit: true, delete: false },
                channels: { view: true, manage: false },
                integrations: { view: true, manage: false },
                analytics: { view: true },
                costs: { view: false },
                api_keys: { view: false, manage: false },
                settings: { view: false, manage: false },
                reputation: { view: true, manage: false },
              },
            });
          }
        }

        // Fetch app status for warnings banner
        try {
          const status = await client.getStatus();
          setAppStatus(status);
        } catch (err) {
          console.error('Failed to fetch app status:', err);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  const handleSetup = async (newApiKey: string, newHostname: string) => {
    try {
      // Store credentials
      localStorage.setItem('apiKey', newApiKey);
      const normalizedHostname = normalizeApiHostname(newHostname);
      localStorage.setItem('apiHostname', normalizedHostname);

      setApiKey(newApiKey);
      setHostname(normalizedHostname);
      const client = new ApiClient(newApiKey, normalizedHostname);
      setApiClient(client);
      setShowSetup(false);
      setError(null);

      // Fetch user info
      try {
        const info = await client.getMe();
        // Merge with defaults for any missing permission categories
        const isAdmin = info.role === 'admin';
        const mergedPermissions = {
          submissions: info.permissions?.submissions ?? { view: true, moderate: true, delete: true, delete_permanent: isAdmin },
          ip: info.permissions?.ip ?? { view: true, manage: true },
          forms: info.permissions?.forms ?? { view: true, create: true, edit: true, delete: isAdmin },
          channels: info.permissions?.channels ?? { view: true, manage: isAdmin },
          integrations: info.permissions?.integrations ?? { view: true, manage: isAdmin },
          analytics: info.permissions?.analytics ?? { view: true },
          costs: info.permissions?.costs ?? { view: isAdmin },
          api_keys: info.permissions?.api_keys ?? { view: isAdmin, manage: isAdmin },
          settings: info.permissions?.settings ?? { view: isAdmin, manage: isAdmin },
          reputation: info.permissions?.reputation ?? { view: true, manage: isAdmin },
        };
        setUserInfo({ ...info, permissions: mergedPermissions });
      } catch (err) {
        // Fallback to old API
        const perms = await client.checkPermissions();
        setUserInfo({
          authenticated: true,
          role: perms.is_admin ? 'admin' : 'user',
          role_label: perms.is_admin ? 'Admin' : 'User',
          permissions: {
            submissions: { view: true, moderate: true, delete: true, delete_permanent: perms.is_admin },
            ip: { view: true, manage: true },
            forms: { view: true, create: true, edit: true, delete: perms.is_admin },
            channels: { view: true, manage: perms.is_admin },
            integrations: { view: true, manage: perms.is_admin },
            analytics: { view: true },
            costs: { view: perms.is_admin },
            api_keys: { view: perms.is_admin, manage: perms.is_admin },
            settings: { view: perms.is_admin, manage: perms.is_admin },
            reputation: { view: true, manage: perms.is_admin },
          },
        });
      }

      // Fetch app status
      try {
        const status = await client.getStatus();
        setAppStatus(status);
      } catch (err) {
        console.error('Failed to fetch app status:', err);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Connecting to Form Pipeline...</p>
      </div>
    );
  }

  if (showSetup) {
    return (
      <SetupScreen onSetup={handleSetup} initialKey={apiKey} initialHostname={hostname} />
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <h2>Connection Error</h2>
        <p>{error}</p>
        <button
          onClick={() => {
            setShowSetup(true);
            setError(null);
          }}
          style={{
            padding: '10px 20px',
            marginTop: '20px',
            background: 'var(--accent)',
            color: 'white',
            border: 'none',
            borderRadius: '7px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Reconfigure Connection
        </button>
      </div>
    );
  }

  if (!apiClient) {
    return null;
  }

  return (
    <PermissionProvider userInfo={userInfo}>
      <AppContent
        apiClient={apiClient}
        appStatus={appStatus}
        hostname={hostname}
        onReconfigure={() => {
          setShowSetup(true);
          setError(null);
        }}
        onLogout={() => {
          localStorage.removeItem('apiKey');
          setApiKey('');
          setApiClient(null);
          setUserInfo(null);
          setAppStatus(null);
          setShowSetup(true);
          setError(null);
        }}
      />
    </PermissionProvider>
  );
}

export default App;
